// color-exempt-file — retokenisation Phase 6 ui-themes : dark:bg-[#hex] preserve le rendu dark historique (voir memoire ui-themes)
// ============================================================
// ⚠️  FICHIER PROTÉGÉ — NE PAS MODIFIER SANS L'ACCORD DE @Julien
// Ce composant implémente le rendu column-based des créneaux horaires
// (fusion visuelle, alignement hauteur, scroll correct).
// Toute modification doit être approuvée avant d'être faite.
// Dernière version stable : 2026-04-02
// Retokenisation couleurs ui-themes approuvée par Julien le 2026-09-08 —
// uniquement les classes de couleur, la structure/layout n'a pas bougé.
// ============================================================
import React, { useRef, forwardRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Clock, Plus } from "lucide-react";
import {
  PollCreatorService,
  TimeSlot,
} from "../../services/PollCreatorService";

interface TimeSlotBlock {
  start: TimeSlot;
  end: {
    hour: number;
    minute: number;
    enabled: boolean;
  };
}

export interface TimeSlotsTableProps {
  selectedDates: string[];
  timeSlotsByDate: Record<string, TimeSlot[]>;
  compactDateHeaders: boolean;
  timeGranularity: number;
  showExtendedHours: boolean;
  mergeMode: boolean;
  onAddTimeSlot: (date: string, hour: number, minute: number) => void;
  onRemoveTimeSlot: (date: string, hour: number, minute: number) => void;
  onToggleExtendedHours: () => void;
  getTimeSlotBlocks: (date: string) => TimeSlotBlock[];
  getVisibleTimeSlots: () => TimeSlot[];
  className?: string;
}

export const TimeSlotsTable = forwardRef<HTMLDivElement, TimeSlotsTableProps>(
  (
    {
      selectedDates,
      timeSlotsByDate,
      compactDateHeaders,
      timeGranularity,
      showExtendedHours,
      mergeMode,
      onAddTimeSlot,
      onRemoveTimeSlot,
      onToggleExtendedHours,
      getTimeSlotBlocks,
      getVisibleTimeSlots,
      className = "",
    },
    ref,
  ) => {
    const { t } = useTranslation("polls");
    const [visibleTimeSlots, setVisibleTimeSlots] = useState<TimeSlot[]>([]);

    useEffect(() => {
      const newSlots = getVisibleTimeSlots();
      setVisibleTimeSlots(newSlots);
    }, [getVisibleTimeSlots, showExtendedHours]);

    return (
      <div
        // Conteneur principal avec scroll horizontal et vertical
        className={`
          overflow-x-auto        // Scroll horizontal visible
          overflow-y-auto        // Scroll vertical visible  
          w-full               // Largeur = 100% du parent
          max-h-96             // Hauteur max pour forcer le scroll vertical
          ${className}
        `}
      >
        {/* Tableau complet avec colonne heure fixe */}
        <div className="flex">
          {/* Colonne Heure - Fixe horizontalement */}
          <div
            className="
          w-16 
          flex-shrink-0 
          sticky 
          left-0 
          z-30"
          >
            {/* En-tête Heure */}
            <div
              className={`
              w-16 
              h-16
              flex-shrink-0
              p-2 
              text-xs 
              font-medium 
              text-muted-foreground
              flex items-center justify-center border-r border-border
              bg-background dark:bg-[#0a0a0a]
              top-0
              z-30
              ${(isNaN(timeGranularity) ? 60 : timeGranularity) >= 60 ? "min-h-[32px]" : "min-h-[24px]"}
            `}
            >
              {t("time_slots_hour_header")}
            </div>
            {/* Cellules Heure */}
            <div data-testid="time-slots-grid">
              {visibleTimeSlots.map((timeSlot) => (
                <div
                  key={`${timeSlot.hour}-${timeSlot.minute}`}
                  className={`
                  w-16 
                  flex-shrink-0 
                  p-2 
                  text-xs 
                  text-muted-foreground
                  flex
                  items-center
                  justify-center
                  border-r
                  border-border
                  bg-background dark:bg-[#0a0a0a]
                  border-b
                  border-border
                  ${(isNaN(timeGranularity) ? 60 : timeGranularity) >= 60 ? "h-8" : "h-6"}
                `}
                  data-time-hour={timeSlot.hour}
                >
                  {timeSlot.label}
                </div>
              ))}
            </div>
          </div>
          {/* Colonnes Dates - Scroll horizontalement */}
          <div className="flex flex-col flex-1">
            {/* En-têtes Dates */}
            <div
              className="
            flex bg-background dark:bg-[#0a0a0a]
            h-16"
            >
              {selectedDates.map((dateStr) => {
                const dateInfo =
                  PollCreatorService.formatSelectedDateHeader(dateStr);
                return (
                  <div
                    key={dateStr}
                    className="
                    flex-1
                    p-2
                    text-center
                    border-r border-border
                    bg-primary
                    text-primary-foreground
                    min-w-[80px]"
                  >
                    <div className="text-xs font-medium">
                      {dateInfo.dayName}
                    </div>
                    <div className="text-sm font-bold">
                      {dateInfo.dayNumber}
                    </div>
                    <div className="text-xs">{dateInfo.month}</div>
                  </div>
                );
              })}
            </div>
            {/* Cellules Créneaux - rendu column-based pour permettre la vraie fusion verticale */}
            <div data-testid="time-slots-grid" className="flex">
              {selectedDates.map((dateStr, colIndex) => {
                const blocks = getTimeSlotBlocks(dateStr);
                const granularity = isNaN(timeGranularity)
                  ? 60
                  : timeGranularity;
                const cellHeight = granularity >= 60 ? 32 : 24;

                return (
                  <div
                    key={dateStr}
                    className={`flex-1 flex flex-col min-w-[80px] ${colIndex < selectedDates.length - 1 ? "border-r border-border" : ""}`}
                  >
                    {visibleTimeSlots.map((timeSlot) => {
                      const slot = timeSlotsByDate[dateStr]?.find(
                        (s) =>
                          s.hour === timeSlot.hour &&
                          s.minute === timeSlot.minute,
                      );
                      const slotMinutes = timeSlot.hour * 60 + timeSlot.minute;

                      // Modèle BLOC (hors fusion) : si un créneau à durée pleine
                      // démarre plus tôt et couvre cette cellule, la masquer pour
                      // laisser le bloc haut (rendu sur sa cellule de début) occuper
                      // la place. Compat rétro : ancien format (duration ≈ granularité)
                      // → aucune cellule couverte, rendu inchangé.
                      if (!mergeMode) {
                        const dateSlots = timeSlotsByDate[dateStr] || [];
                        const covered = dateSlots.some((s) => {
                          if (!s.enabled) return false;
                          const sStart = s.hour * 60 + s.minute;
                          const sDur =
                            (s as any).duration && (s as any).duration > 0
                              ? (s as any).duration
                              : granularity;
                          return (
                            slotMinutes > sStart && slotMinutes < sStart + sDur
                          );
                        });
                        if (covered) {
                          return null;
                        }
                      }

                      // En mode fusion, calculer si cette cellule fait partie d'un bloc
                      if (mergeMode) {
                        const block = blocks.find((b) => {
                          const bStart = b.start.hour * 60 + b.start.minute;
                          const bEnd = b.end.hour * 60 + b.end.minute;
                          return slotMinutes >= bStart && slotMinutes < bEnd;
                        });

                        if (block) {
                          const blockStartMinutes =
                            block.start.hour * 60 + block.start.minute;
                          const blockEndMinutes =
                            block.end.hour * 60 + block.end.minute;
                          const slotsInBlock = Math.round(
                            (blockEndMinutes - blockStartMinutes) / granularity,
                          );
                          const isFirst = slotMinutes === blockStartMinutes;

                          if (!isFirst) {
                            // Ce créneau est couvert par le bloc fusionné au-dessus - ne pas rendre
                            return null;
                          }

                          if (slotsInBlock > 1) {
                            // Rendu du bloc fusionné : un seul élément haut de N × cellHeight
                            const startTime = `${block.start.hour.toString().padStart(2, "0")}:${block.start.minute.toString().padStart(2, "0")}`;
                            const endTime = `${block.end.hour.toString().padStart(2, "0")}:${block.end.minute.toString().padStart(2, "0")}`;

                            return (
                              <button
                                key={`${timeSlot.hour}-${timeSlot.minute}`}
                                className="fused-cell w-full bg-primary hover:bg-primary/90 text-primary-foreground text-xs flex items-center justify-center transition-colors rounded border-b border-primary/60"
                                style={{
                                  height: `${slotsInBlock * cellHeight}px`,
                                }}
                                data-date={dateStr}
                                data-hour={timeSlot.hour}
                                data-minute={timeSlot.minute}
                                data-selected="true"
                                data-fused="true"
                                data-colspan={slotsInBlock}
                                title={`${startTime} - ${endTime}`}
                                onClick={() =>
                                  onRemoveTimeSlot(
                                    dateStr,
                                    timeSlot.hour,
                                    timeSlot.minute,
                                  )
                                }
                              >
                                <span className="font-semibold">
                                  {startTime}
                                </span>
                                <span className="mx-1 opacity-75">-</span>
                                <span className="opacity-75">{endTime}</span>
                              </button>
                            );
                          }
                          // slotsInBlock <= 1 : affichage normal ci-dessous
                        }
                      }

                      // Créneau sélectionné normal
                      const isSelected = slot?.enabled ?? false;

                      if (isSelected) {
                        // Modèle BLOC : durée pleine du créneau fait foi.
                        // Compat rétro : duration absent → granularité (1 cellule).
                        const slotDuration =
                          (slot as any)?.duration && (slot as any).duration > 0
                            ? (slot as any).duration
                            : granularity;
                        const slotSpan = Math.max(
                          1,
                          Math.round(slotDuration / granularity),
                        );
                        const endMinutes = slotMinutes + slotDuration;
                        const endHour = Math.floor(endMinutes / 60);
                        const endMin = endMinutes % 60;
                        const endTime = `${endHour.toString().padStart(2, "0")}:${endMin.toString().padStart(2, "0")}`;
                        const startTime = `${timeSlot.hour.toString().padStart(2, "0")}:${timeSlot.minute.toString().padStart(2, "0")}`;

                        return (
                          <button
                            key={`${timeSlot.hour}-${timeSlot.minute}`}
                            className={`time-cell w-full ${granularity >= 60 ? "p-1" : "p-0.5"} bg-primary hover:bg-primary/90 text-primary-foreground text-xs rounded flex items-center justify-center transition-colors border-b border-border`}
                            style={{ height: `${slotSpan * cellHeight}px` }}
                            data-date={dateStr}
                            data-hour={timeSlot.hour}
                            data-minute={timeSlot.minute}
                            data-selected="true"
                            data-fused="false"
                            data-hidden="false"
                            title={`${startTime} - ${endTime}`}
                            onClick={() =>
                              onRemoveTimeSlot(
                                dateStr,
                                timeSlot.hour,
                                timeSlot.minute,
                              )
                            }
                          >
                            <div className="text-center">
                              <span className="font-semibold">{startTime}</span>
                              <span className="text-xs opacity-75 mx-1">→</span>
                              <span className="text-xs opacity-75">
                                {endTime}
                              </span>
                            </div>
                          </button>
                        );
                      }

                      // Créneau vide / non sélectionné
                      return (
                        <button
                          key={`${timeSlot.hour}-${timeSlot.minute}`}
                          className={`time-cell w-full border-b border-border transition-colors hover:bg-accent dark:hover:bg-[#2a2a2a] ${slot ? "bg-primary/10" : "bg-card dark:bg-[#1e1e1e]"}`}
                          style={{ height: `${cellHeight}px` }}
                          data-date={dateStr}
                          data-hour={timeSlot.hour}
                          data-minute={timeSlot.minute}
                          data-selected="false"
                          data-fused="false"
                          data-hidden="false"
                          title={`Ajouter ${timeSlot.hour.toString().padStart(2, "0")}:${timeSlot.minute.toString().padStart(2, "0")}`}
                          onClick={() =>
                            onAddTimeSlot(
                              dateStr,
                              timeSlot.hour,
                              timeSlot.minute,
                            )
                          }
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        {/* Bouton Afficher plus d'horaires */}
        <div className="p-3 bg-background dark:bg-[#0a0a0a] border-t border-border">
          <button
            onClick={onToggleExtendedHours}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <Plus className="w-3 h-3" />
            <span>
              {showExtendedHours
                ? t("time_slots_hide_extended")
                : t("time_slots_show_more")}
            </span>
          </button>
        </div>
      </div>
    );
  },
);

TimeSlotsTable.displayName = "TimeSlotsTable";
